import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/api/useApi";
import type {
  CreatePaymentIntentRequest,
  CreatePaymentIntentResponse,
  CreateCustomerPortalSessionRequest,
  CreateCustomerPortalSessionResponse,
  Invoice,
  UsageData,
  UsageRequestParams,
  UsageBasedPlan,
} from "./Billing.model";

// Query Keys
const BILLING_USAGE_QUERY_KEY = "billing-usage";
const BILLING_INVOICES_QUERY_KEY = "billing-invoices";

// Constants
export const USAGE_BASED_PLAN: UsageBasedPlan = {
  id: "price_1S6xsKFLQjJOKYpH5M2ML2Np",
  name: "Pay-as-you-go",
  baseAmount: 2000,
  currency: "usd",
  interval: "month",
  includedUsage: 500,
  overage: {
    priceId: "price_OVERAGE_PRICE_NEEDED",
    unitAmount: 4,
    unitName: "request",
  },
  features: [
    "500 free requests/month",
    "$0.04 per additional request",
    "All integrations included",
    "Email & chat support",
    "Real-time analytics",
  ],
};

export const USAGE_BASED_PLAN_ANNUAL: UsageBasedPlan = {
  id: "price_annual_not_supported",
  name: "Pay-as-you-go (Annual)",
  baseAmount: 19200,
  currency: "usd",
  interval: "year",
  includedUsage: 500,
  overage: {
    priceId: "price_OVERAGE_PRICE_NEEDED",
    unitAmount: 4,
    unitName: "request",
  },
  features: [
    "500 free requests/month",
    "$0.04 per additional request",
    "All integrations included",
    "Email & chat support",
    "Real-time analytics",
    "20% annual discount on base fee",
  ],
};

// Utility Functions
export const formatCurrency = (amount?: number): string => {
  if (!amount) {
    return "$0.00";
  }
  return `$${(amount / 100).toFixed(2)}`;
};

export const formatQuantity = (value?: number): string => {
  return (value ?? 0).toLocaleString("en-US");
};

export const formatDate = (timestamp: number): string => {
  if (!timestamp) {
    return "-";
  }
  const normalized = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  return new Date(normalized).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// API Functions
const createPaymentIntent = async (
  request: CreatePaymentIntentRequest,
): Promise<CreatePaymentIntentResponse> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return {
    clientSecret: `pi_mock_${Date.now()}_secret_mock`,
    paymentIntentId: `pi_mock_${Date.now()}`,
  };
};

const createCustomerPortalSession = async (
  api: AxiosInstance,
  request: CreateCustomerPortalSessionRequest,
): Promise<CreateCustomerPortalSessionResponse> => {
  const { data } = await api.post<CreateCustomerPortalSessionResponse>(
    "/platform/stripe/customer-portal-session",
    request,
  );
  return data;
};

const getInvoices = async (api: AxiosInstance): Promise<Invoice[]> => {
  const { data } = await api.get<Invoice[]>("/platform/billing/invoices");
  return data;
};

const getCurrentUsage = async (
  api: AxiosInstance,
  params?: UsageRequestParams,
): Promise<UsageData> => {
  const query = new URLSearchParams();

  if (params?.period) {
    query.set("period", params.period);
  }
  if (typeof params?.startTime === "number") {
    query.set("startTime", params.startTime.toString());
  }
  if (typeof params?.endTime === "number") {
    query.set("endTime", params.endTime.toString());
  }
  if (params?.grouping) {
    query.set("grouping", params.grouping);
  }

  const { data } = await api.get<UsageData>(
    `/platform/billing/usage${query.toString() ? `?${query.toString()}` : ""}`,
  );
  return data;
};

const createUpgradeSession = async (
  customerId: string,
  newPriceId: string,
  returnUrl?: string,
): Promise<{ url: string }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));

  return {
    url: `https://checkout.stripe.com/pay/cs_test_upgrade_session_${newPriceId}`,
  };
};

// Custom Hook
export function useBillingApi(usageParams?: UsageRequestParams) {
  const { api } = useApi();

  // Query keys
  const usageQueryKey = useMemo(
    () => [
      BILLING_USAGE_QUERY_KEY,
      usageParams?.period,
      usageParams?.startTime,
      usageParams?.endTime,
      usageParams?.grouping,
    ],
    [usageParams?.endTime, usageParams?.grouping, usageParams?.period, usageParams?.startTime],
  );

  const invoicesQueryKey = useMemo(() => [BILLING_INVOICES_QUERY_KEY], []);

  // Queries
  const usageQuery = useQuery<UsageData, Error>(usageQueryKey, () => getCurrentUsage(api, usageParams), {
    staleTime: 60_000,
    cacheTime: 5 * 60_000,
    keepPreviousData: true,
    enabled: !!api,
  });

  const invoicesQuery = useQuery<Invoice[], Error>(invoicesQueryKey, () => getInvoices(api), {
    staleTime: 60_000,
    cacheTime: 5 * 60_000,
    keepPreviousData: true,
    enabled: !!api,
  });

  // Mutations and handlers
  const openCustomerPortal = useCallback(
    async (customerId?: string) => {
      try {
        const session = await createCustomerPortalSession(api, {
          customerId: customerId || "demo_customer_id",
          returnUrl: `${window.location.origin}/settings/billing`,
        });

        const newWindow = window.open(session.url, "_blank");
        if (!newWindow || newWindow.closed) {
          window.location.href = session.url;
        }
      } catch (error) {
        console.error("Failed to open Stripe Customer Portal:", error);
        alert("Unable to open billing portal. Please try again later.");
      }
    },
    [api],
  );

  const viewInvoice = useCallback(
    async (invoiceId: string) => {
      try {
        const invoices = await getInvoices(api);
        const invoice = invoices.find((inv) => inv.id === invoiceId);

        if (invoice?.hosted_invoice_url) {
          window.open(invoice.hosted_invoice_url, "_blank");
        } else {
          console.error("Invoice URL not found");
          alert("Invoice not available");
        }
      } catch (error) {
        console.error("Failed to view invoice:", error);
        alert("Unable to open invoice. Please try again later.");
      }
    },
    [api],
  );

  const downloadInvoicePDF = useCallback(
    async (invoiceId: string) => {
      try {
        const invoices = await getInvoices(api);
        const invoice = invoices.find((inv) => inv.id === invoiceId);

        if (invoice?.invoice_pdf) {
          window.open(invoice.invoice_pdf, "_blank");
        } else {
          console.error("Invoice PDF not found");
          alert("Invoice PDF not available");
        }
      } catch (error) {
        console.error("Failed to download invoice PDF:", error);
        alert("Unable to download invoice. Please try again later.");
      }
    },
    [api],
  );

  const upgradeSubscription = useCallback(async (newPriceId: string) => {
    try {
      const session = await createUpgradeSession(
        "demo_customer_id",
        newPriceId,
        `${window.location.origin}/settings/billing?upgraded=true`,
      );

      window.location.href = session.url;
    } catch (error) {
      console.error("Failed to upgrade subscription:", error);
      alert("Unable to process upgrade. Please try again later.");
    }
  }, []);

  const handleCreatePaymentIntent = useCallback(
    async (request: CreatePaymentIntentRequest) => {
      return createPaymentIntent(request);
    },
    [],
  );

  return {
    // Usage data
    usage: {
      data: usageQuery.data ?? null,
      isLoading: usageQuery.isLoading,
      error: usageQuery.error ?? null,
      refetch: usageQuery.refetch,
    },
    // Invoices data
    invoices: {
      data: invoicesQuery.data ?? [],
      isLoading: invoicesQuery.isLoading,
      error: invoicesQuery.error ?? null,
      refetch: invoicesQuery.refetch,
    },
    // Actions
    openCustomerPortal: openCustomerPortal,
    viewInvoice,
    downloadInvoicePDF,
    upgradeSubscription,
    createPaymentIntent: handleCreatePaymentIntent,
  };
}

